import React, { useState, useRef, useCallback } from 'react';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import MapPin from 'lucide-react/dist/esm/icons/map-pin';
import Search from 'lucide-react/dist/esm/icons/search';
import Globe from 'lucide-react/dist/esm/icons/globe';
import Star from 'lucide-react/dist/esm/icons/star';
import Bot from 'lucide-react/dist/esm/icons/bot';
import Check from 'lucide-react/dist/esm/icons/check';
import CheckCheck from 'lucide-react/dist/esm/icons/check-check';
import Plus from 'lucide-react/dist/esm/icons/plus';
import RotateCcw from 'lucide-react/dist/esm/icons/rotate-ccw';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2';
import X from 'lucide-react/dist/esm/icons/x';
import Radar from 'lucide-react/dist/esm/icons/radar';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right';
import {
  Card, Button, Text, Badge, Spinner, TextField, Select, EmptyState, useToast,
} from 'components/ui';
import { salesApi } from '../../services/salesApi';
import { useLeadGeneratorStore } from '../../stores/leadGeneratorStore';
import { useSalesStore } from '../../stores/salesStore';
import { useUIStore } from 'stores';
import { getTranslation } from 'i18n';
import type { GeneratorSSEEvent, LeadQuality } from '../../types/sales';

const COUNTRIES = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱' },
  { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming',
];

const QUALITY_STYLES: Record<LeadQuality, { badge: 'success' | 'warning' | 'danger'; bar: string }> = {
  high: { badge: 'success', bar: 'bg-emerald-500' },
  medium: { badge: 'warning', bar: 'bg-amber-500' },
  low: { badge: 'danger', bar: 'bg-red-400' },
};

const LeadGenerator: React.FC = () => {
  const toast = useToast();
  const { fetchLeads } = useSalesStore();
  const language = useUIStore((s) => s.language);
  const t = (key: string) => getTranslation(language, key);
  const {
    country, setCountry, state, setState, city, setCity,
    distanceKm, setDistanceKm, industryKeywords, setIndustryKeywords,
    maxResults, setMaxResults, phase, setPhase,
    discoveredLeads, setDiscoveredLeads, addDiscoveredLead,
    isComplete, setIsComplete, summary, setSummary,
    statusMessage, setStatusMessage,
    toggleLeadSelection, toggleSelectAll, resetSearch,
  } = useLeadGeneratorStore();

  const [isAccepting, setIsAccepting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const selectedCountry = COUNTRIES.find((c) => c.code === country);
  const isUSA = country === 'US';
  const canGenerate = country.length > 0;
  const selectedCount = discoveredLeads.filter((l) => l.selected).length;

  const handleGenerate = useCallback(async () => {
    setPhase('running');
    setDiscoveredLeads([]);
    setIsComplete(false);
    setSummary('');
    setStatusMessage(t('salesGeneratorInitMessage'));

    const controller = new AbortController();
    abortRef.current = controller;
    let streamFinalized = false;
    let localCount = 0;

    try {
      const response = await salesApi.generate({
        country: selectedCountry?.name || country,
        state: isUSA && state ? state : undefined,
        city: city || undefined,
        distance_km: city ? distanceKm : undefined,
        industry_keywords: industryKeywords || undefined,
        max_results: maxResults,
      });

      if (!response.ok) {
        let msg = `Server error: ${response.status}`;
        try { const d = await response.json(); if (d.detail) msg = d.detail; } catch {}
        throw new Error(msg);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response stream');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        if (controller.signal.aborted) break;
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event: GeneratorSSEEvent = JSON.parse(line.slice(6));
            if (event.type === 'status') {
              setStatusMessage(event.message || '');
            } else if (event.type === 'lead_found' && event.lead) {
              const lead = event.lead;
              const quality = lead.quality || (lead.ai_rating >= 4 ? 'high' : lead.ai_rating >= 3 ? 'medium' : 'low');
              addDiscoveredLead({ ...lead, quality, selected: quality !== 'low' });
              localCount++;
            } else if (event.type === 'complete') {
              streamFinalized = true;
              setIsComplete(true);
              setSummary(event.summary || t('salesGeneratorFoundSummary').replace('{{count}}', String(event.total_found ?? 0)));
              setStatusMessage('');
              setPhase('results');
            } else if (event.type === 'error') {
              streamFinalized = true;
              setSummary(event.message || t('salesGeneratorErrorGenerateFailed'));
              toast.error(event.message || t('salesGeneratorErrorGenerateFailed'));
              setStatusMessage('');
              setPhase('results');
              setIsComplete(true);
            }
          } catch { /* skip malformed */ }
        }
      }

      if (!streamFinalized && !controller.signal.aborted) {
        setIsComplete(true);
        setSummary(localCount > 0
          ? t('salesGeneratorStreamEndedEarly').replace('{{count}}', String(localCount))
          : t('salesGeneratorNoResults'));
        setStatusMessage('');
        setPhase('results');
      }
    } catch (err: any) {
      if (!controller.signal.aborted) {
        toast.error(err.message || t('salesGeneratorErrorGenerateFailed'));
        setSummary(err.message || 'Failed');
        setPhase('config');
      }
    }
  }, [country, state, city, distanceKm, industryKeywords, maxResults, selectedCountry, isUSA,
      setPhase, setDiscoveredLeads, setIsComplete, setSummary, setStatusMessage, addDiscoveredLead, toast]);

  const handleCancel = () => {
    abortRef.current?.abort();
    setPhase('config');
    setStatusMessage('');
  };

  const handleAcceptLeads = async () => {
    const leadsToAccept = discoveredLeads
      .filter((l) => l.selected)
      .map(({ selected, quality, ...lead }) => lead);

    if (leadsToAccept.length === 0) { toast.error(t('salesGeneratorErrorNoLeads')); return; }

    setIsAccepting(true);
    try {
      await salesApi.acceptLeads(leadsToAccept);
      fetchLeads();
      toast.success(t('salesGeneratorToastLeadsAdded').replace('{{count}}', String(leadsToAccept.length)));
      resetSearch();
    } catch (err: any) {
      toast.error(err.message || t('salesGeneratorErrorAddFailed'));
    } finally {
      setIsAccepting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg">
          <Sparkles size={18} className="text-white" />
        </div>
        <div>
          <Text variant="subheading" className="text-lg">{t('salesGeneratorTitle')}</Text>
          <p className="text-xs text-content-muted">{t('salesGeneratorSubtitle')}</p>
        </div>
      </div>

      {/* ── CONFIG PHASE ── */}
      {phase === 'config' && (
        <div className="space-y-5">
          {/* Location */}
          <Card className="p-5 border-t-2 border-t-purple-500/50">
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={16} className="text-purple-500" />
              <Text variant="subheading" className="text-sm">{t('salesGeneratorCardLocation')}</Text>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-content-secondary mb-1.5">
                  {t('salesGeneratorLabelCountry')} <span className="text-status-error">*</span>
                </label>
                <Select
                  value={country}
                  onChange={setCountry}
                  options={[
                    { value: '', label: t('salesGeneratorPlaceholderCountry') },
                    ...COUNTRIES.map((c) => ({ value: c.code, label: `${c.flag} ${c.name}` })),
                  ]}
                  placeholder="Select country..."
                />
              </div>

              {isUSA && (
                <div>
                  <label className="block text-xs font-medium text-content-secondary mb-1.5">{t('salesGeneratorLabelState')}</label>
                  <Select
                    value={state}
                    onChange={setState}
                    options={[
                      { value: '', label: t('salesGeneratorPlaceholderState') },
                      ...US_STATES.map((s) => ({ value: s, label: s })),
                    ]}
                    placeholder="All states"
                  />
                </div>
              )}

              {country && (
                <div>
                  <TextField
                    label={t('salesGeneratorLabelCity')}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder={t('salesGeneratorPlaceholderCity')}
                  />
                </div>
              )}

              {city.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-content-secondary mb-1.5">
                    {t('salesGeneratorLabelDistance').replace('{{value}}', String(distanceKm))}
                  </label>
                  <input
                    type="range" min={10} max={500} step={10}
                    value={distanceKm}
                    onChange={(e) => setDistanceKm(Number(e.target.value))}
                    className="w-full accent-purple-500 mt-2"
                  />
                </div>
              )}
            </div>
          </Card>

          {/* Refinement */}
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Search size={16} className="text-content-muted" />
              <Text variant="subheading" className="text-sm">{t('salesGeneratorCardRefinement')}</Text>
              <span className="text-[10px] text-content-muted">{t('salesGeneratorOptionalLabel')}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <TextField
                  label={t('salesGeneratorLabelKeywords')}
                  value={industryKeywords}
                  onChange={(e) => setIndustryKeywords(e.target.value)}
                  placeholder={t('salesGeneratorPlaceholderKeywords')}
                  hint={t('salesGeneratorHintKeywords')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-content-secondary mb-1.5">{t('salesGeneratorLabelMaxResults')}</label>
                <div className="flex items-center gap-2 mt-1">
                  {[4, 6, 12].map((n) => (
                    <button
                      key={n}
                      onClick={() => setMaxResults(n)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-none cursor-pointer ${
                        maxResults === n
                          ? 'bg-purple-500/15 text-purple-400 ring-1 ring-purple-500/30'
                          : 'bg-surface-secondary text-content-muted hover:text-content-secondary'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          <div className="flex justify-center">
            <Button
              variant="primary"
              size="lg"
              onClick={handleGenerate}
              disabled={!canGenerate}
              leftIcon={<Sparkles size={16} />}
            >
              {t('salesGeneratorBtnGenerate')}
            </Button>
          </div>

          <div className="p-3 bg-purple-500/5 rounded-xl border border-purple-500/10 text-center">
            <p className="text-xs text-purple-400">
              {t('salesGeneratorInfoText')}
            </p>
          </div>
        </div>
      )}

      {/* ── RUNNING / RESULTS PHASE ── */}
      {(phase === 'running' || phase === 'results') && (
        <div>
          {/* Status bar */}
          {phase === 'running' && (
            <Card className="p-4 mb-5 border-l-4 border-l-purple-500">
              <div className="flex items-center gap-3">
                <Radar size={20} className="text-purple-500 animate-spin" style={{ animationDuration: '3s' }} />
                <div className="flex-1 min-w-0">
                  <Text variant="subheading" className="text-sm">{t('salesGeneratorRunningTitle')}</Text>
                  <p className="text-xs text-content-muted truncate">{statusMessage}</p>
                </div>
                <span className="text-sm font-medium text-purple-400">{t('salesGeneratorFoundCount').replace('{{count}}', String(discoveredLeads.length))}</span>
                <button onClick={handleCancel} className="p-1.5 hover:bg-surface-secondary rounded-lg transition-colors border-none bg-transparent cursor-pointer">
                  <X size={16} className="text-content-muted" />
                </button>
              </div>
            </Card>
          )}

          {/* Complete summary */}
          {isComplete && discoveredLeads.length > 0 && (
            <Card className="p-4 mb-5 border-l-4 border-l-emerald-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <Check size={16} className="text-emerald-400" />
                  </div>
                  <div>
                    <Text variant="subheading" className="text-sm">{summary}</Text>
                    <p className="text-xs text-content-muted">
                      {discoveredLeads.filter((l) => l.quality === 'high').length} high · {discoveredLeads.filter((l) => l.quality === 'medium').length} medium · {discoveredLeads.filter((l) => l.quality === 'low').length} low · {selectedCount}/{discoveredLeads.length} selected
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { resetSearch(); }} leftIcon={<RotateCcw size={14} />}>
                  {t('salesGeneratorBtnNewSearch')}
                </Button>
              </div>
            </Card>
          )}

          {phase === 'results' && discoveredLeads.length === 0 && (
            <EmptyState
              title={t('salesGeneratorEmptyTitle')}
              description={summary || t('salesGeneratorEmptyDesc')}
              action={
                <Button variant="primary" size="sm" onClick={() => resetSearch()} leftIcon={<RotateCcw size={14} />}>
                  {t('salesGeneratorBtnTryAgain')}
                </Button>
              }
            />
          )}

          {/* Results grid */}
          {discoveredLeads.length > 0 && (
            <>
              {isComplete && (
                <div className="flex items-center justify-between mb-4 p-3 rounded-lg bg-surface-primary border border-subtle">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={toggleSelectAll}
                      className="text-xs text-content-secondary hover:text-content flex items-center gap-1.5 transition-colors border-none bg-transparent cursor-pointer"
                    >
                      <CheckCheck size={14} />
                      {discoveredLeads.every((l) => l.selected) ? t('salesGeneratorBtnDeselectAll') : t('salesGeneratorBtnSelectAll')}
                    </button>
                    <span className="text-xs text-content-muted">
                      {t('salesGeneratorSelectedCount').replace('{{selected}}', String(selectedCount)).replace('{{total}}', String(discoveredLeads.length))}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => resetSearch()}>{t('salesGeneratorBtnDiscard')}</Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleAcceptLeads}
                      disabled={selectedCount === 0 || isAccepting}
                      loading={isAccepting}
                      leftIcon={<Plus size={14} />}
                    >
                      {t('salesGeneratorBtnAddToPipeline').replace('{{count}}', String(selectedCount))}
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {discoveredLeads.map((lead, index) => {
                  const qs = QUALITY_STYLES[lead.quality];
                  return (
                    <Card
                      key={`${lead.website_url}-${index}`}
                      className={`p-4 cursor-pointer transition-all duration-200 border-l-[3px] ${
                        lead.selected
                          ? 'border-l-purple-500 ring-1 ring-purple-500/20'
                          : 'border-l-transparent opacity-50 hover:opacity-70'
                      }`}
                      onClick={() => toggleLeadSelection(index)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0 mr-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Building2 size={14} className="text-purple-500 flex-shrink-0" />
                            <span className="text-sm font-medium text-content truncate">{lead.company_name}</span>
                          </div>
                          <div className="text-[10px] text-content-muted flex items-center gap-1 truncate">
                            <Globe size={10} className="flex-shrink-0" />
                            {lead.website_url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                          </div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          lead.selected ? 'bg-purple-500 border-purple-500' : 'border-subtle'
                        }`}>
                          {lead.selected && <Check size={12} className="text-white" />}
                        </div>
                      </div>

                      <p className="text-xs text-content-secondary line-clamp-2 mb-2">{lead.business_info}</p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Bot size={12} className="text-purple-400" />
                          <span className="inline-flex gap-px">
                            {[1, 2, 3, 4, 5].map((i) => (
                              <Star key={i} size={11} className={i <= lead.ai_rating ? 'text-amber-400 fill-amber-400' : 'text-content-faint'} strokeWidth={1.5} />
                            ))}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-14 h-1 bg-surface-secondary rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${qs.bar}`} style={{ width: `${(lead.ai_rating / 5) * 100}%` }} />
                          </div>
                          <Badge variant={qs.badge} size="sm">{lead.quality}</Badge>
                        </div>
                      </div>

                      <div className="mt-2 pt-2 border-t border-subtle">
                        <p className="text-[10px] text-content-muted line-clamp-2">{lead.ai_analysis}</p>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          )}

        </div>
      )}
    </div>
  );
};

export default LeadGenerator;
